// Asset tag card (asset-detail-revamp-handover §3.6): the QR the printed label carries (same URL as Print label —
// lib/labels labelUrl), the code under it, the electronic tag(s) on the asset, and whether the mounting photo is on
// file. The mounting photo is taken on the phone when the tag is installed (install mode, managers); the web only
// shows it — owner decision 2026-09-24.
import { palette } from '@fleetspoke/ui/tokens';
import { Box, Link, Skeleton, Stack, Typography } from '@mui/material';
import QRCode from 'qrcode';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { MountingPhoto } from '../../data/photos';
import { type AssetTag, ELECTRONIC_TAG_TYPES } from '../../data/lifecycle';
import { labelUrl } from '../../lib/labels';
import { appConfig } from '../../lib/supabase';
import { Card, CardHeader } from './Card';

function useQr(text: string | null) {
  const [src, setSrc] = useState<string | null>(null);
  useEffect(() => {
    let alive = true;
    if (!text) return;
    void QRCode.toDataURL(text, { errorCorrectionLevel: 'M', margin: 1, width: 192 }).then(
      (url: string) => alive && setSrc(url),
      () => alive && setSrc(null),
    );
    return () => {
      alive = false;
    };
  }, [text]);
  return src;
}

/** What the printed label encodes; null when there is no company code or no public URL configured. */
function qrText(companyCode: string | null | undefined, assetCode: string): string | null {
  if (!companyCode) return null;
  try {
    return labelUrl(appConfig().publicUrl, companyCode, assetCode);
  } catch {
    return null; // the card still names the tag, without the picture
  }
}

export function TagCard({
  assetCode,
  companyCode,
  tags,
  mounting,
  loading,
}: {
  assetCode: string;
  companyCode: string | null | undefined;
  tags: AssetTag[];
  mounting: MountingPhoto[];
  loading: boolean;
}) {
  const { t } = useTranslation();
  const qr = useQr(qrText(companyCode, assetCode));
  const devices = tags.filter((x) => (ELECTRONIC_TAG_TYPES as readonly string[]).includes(x.type));
  const photo = mounting[0];
  const status = !devices.length
    ? t('asset.tag_qr_only')
    : photo
      ? t('asset.tag_mounted_photo')
      : t('asset.tag_mounted_no_photo');

  return (
    <Card id="asset-tag" data-testid="asset-tag" sx={{ display: 'flex', flexDirection: 'column' }}>
      <CardHeader title={t('asset.tag_title')} />
      <Stack alignItems="center" spacing={1} sx={{ flex: 1, justifyContent: 'center' }}>
        <Box
          sx={{
            width: 104,
            height: 104,
            p: 1,
            border: 1,
            borderColor: 'divider',
            borderRadius: 2,
            bgcolor: palette.surface.light, // a QR code needs a light quiet zone in dark mode too
            display: 'grid',
            placeItems: 'center',
          }}
        >
          {qr ? (
            <Box
              component="img"
              src={qr}
              alt={t('asset.tag_qr_alt', { code: assetCode })}
              sx={{ width: '100%', height: '100%' }}
            />
          ) : (
            <Skeleton variant="rectangular" width={86} height={86} />
          )}
        </Box>
        <Typography
          sx={{ fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace', fontSize: 13 }}
        >
          {assetCode}
        </Typography>
        {loading ? (
          <Skeleton width={140} />
        ) : (
          <>
            {devices.map((d) => (
              <Typography
                key={d.id}
                variant="body2"
                color="text.secondary"
                data-testid="tag-device"
              >
                {t(`reports.tag_type_${d.type}`)} ·{' '}
                <Box
                  component="span"
                  sx={{ fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace' }}
                >
                  {d.identifier}
                </Box>
              </Typography>
            ))}
            <Typography variant="body2" color="text.secondary" data-testid="tag-status">
              {status}
            </Typography>
            {photo?.url ? (
              <Link href={photo.url} target="_blank" rel="noreferrer" variant="body2">
                {t('asset.tag_view_photo')}
              </Link>
            ) : devices.length ? (
              <Typography variant="caption" color="text.secondary" textAlign="center">
                {t('asset.tag_photo_on_phone')}
              </Typography>
            ) : null}
          </>
        )}
      </Stack>
    </Card>
  );
}
